import React, { useState, useEffect } from 'react';
import { 
  Calendar as CalendarIcon, Clock, Plus, ChevronLeft, ChevronRight,
  Radio, Video, BookOpen, Camera, BarChart3, FileText, Check, X,
  AlertCircle, Users, Zap, Edit2, Trash2, Eye, ListChecks, ClipboardList, XCircle,
  Target, TrendingUp, StickyNote, ThumbsUp, ThumbsDown, Copy, LayoutGrid, List, GripVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import ActivitySummaryReportModal from '@/components/activity/ActivitySummaryReportModal';
import ActivityRemindersPanel from '@/components/ActivityRemindersPanel';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Current user accessor (for completed_by attribution)
const getCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};
const isManagerRole = (u) => ['admin', 'manager', 'developer'].includes(u?.role_id);

// Section Header Component - STANDARDIZED
function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white font-heading">{title}</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

// Sortable wrapper for Daily Schedule activity cards. Applies dnd-kit
// transform/transition + exposes a drag handle at the top-left of each card.
function SortableActivityCard({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : 'auto',
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <button
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="absolute left-1 top-1/2 -translate-y-1/2 z-10 p-1 rounded hover:bg-white/10 cursor-grab active:cursor-grabbing opacity-40 hover:opacity-100 transition-opacity"
        title="Drag to reorder"
        data-testid={`drag-handle-${id}`}
      >
        <GripVertical size={14} className="text-[#9CA3AF]" />
      </button>
      <div className="pl-5">{children}</div>
    </div>
  );
}

// Activity type configurations
const activityTypes = {
  livetrade: { label: 'Live Trade', icon: Radio, color: '#E63946', bgColor: 'rgba(230, 57, 70, 0.15)' },
  livesession: { label: 'Live Session', icon: Video, color: '#3B82F6', bgColor: 'rgba(59, 130, 246, 0.15)' },
  webinar: { label: 'Webinar/Class', icon: BookOpen, color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.15)' },
  shooting_content: { label: 'Shooting Content', icon: Camera, color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.15)' },
  publish_content: { label: 'Publish Content', icon: FileText, color: '#A855F7', bgColor: 'rgba(168, 85, 247, 0.15)' },
  // Legacy alias for old records that used 'shooting' as the type key.
  // Kept here so existing rows still render with the correct icon/colour;
  // new activities default to 'shooting_content'.
  shooting: { label: 'Shooting Content', icon: Camera, color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.15)' },
  signal: { label: 'Signal', icon: BarChart3, color: '#8B5CF6', bgColor: 'rgba(139, 92, 246, 0.15)' },
  other: { label: 'Other', icon: FileText, color: '#6B7280', bgColor: 'rgba(107, 114, 128, 0.15)' },
};

// Types that benefit from a per-record Quantity input (counted into scorecard).
const QUANTITY_TYPES = new Set(['shooting_content', 'publish_content', 'shooting']);

// Status configurations
const statusConfig = {
  planned: { label: 'Planned', color: '#3B82F6', icon: Clock },
  completed: { label: 'Completed', color: '#10B981', icon: Check },
  missed: { label: 'Missed', color: '#E63946', icon: X },
  rejected: { label: 'Rejected', color: '#6B7280', icon: XCircle },
};

// Approval status (PM workflow)
const approvalConfig = {
  pending: { label: 'Pending Approval', color: '#F59E0B' },
  approved: { label: 'Approved', color: '#10B981' },
  rejected: { label: 'Rejected', color: '#E63946' },
};

// Channel theme — segmented toggle visual identity
const channelTheme = {
  public: { label: 'PUBLIC', icon: Users, color: '#3B82F6', accent: 'from-[#3B82F6]/30 to-[#06B6D4]/20', border: 'border-[#3B82F6]/40', shadow: 'shadow-[0_0_15px_-3px_#3B82F6]' },
  vip:    { label: 'VIP',    icon: Zap,   color: '#F59E0B', accent: 'from-[#F59E0B]/30 to-[#EAB308]/20', border: 'border-[#F59E0B]/40', shadow: 'shadow-[0_0_15px_-3px_#F59E0B]' },
};

// Days of week
const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function ChannelActivityContent({ ib }) {
  const [channelType, setChannelType] = useState('public');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Copy Month modal — bulk-duplicate every planned activity in the current
  // viewing month to another target month (e.g. June → July).
  const [showCopyMonthModal, setShowCopyMonthModal] = useState(false);
  const [copyMonthTarget, setCopyMonthTarget] = useState(''); // 'YYYY-MM'
  const [copyMonthIncludeCompleted, setCopyMonthIncludeCompleted] = useState(false);
  const [showSummaryReport, setShowSummaryReport] = useState(false);
  const [filterType, setFilterType] = useState('all');
  // View mode toggle for the Upcoming Activities section — grid or list
  const [upcomingView, setUpcomingView] = useState(() => {
    try { return localStorage.getItem('activity_upcoming_view') || 'grid'; } catch { return 'grid'; }
  });
  
  // Check if this is FIRA IND - show "no data" instead of mock
  const isFiraInd = ib?.id === 'ib-fira-001';
  
  // Form state for new activity
  const [newActivity, setNewActivity] = useState({
    title: '',
    type: 'livetrade',
    date: '',
    startTime: '',
    endTime: '',
    description: '',
    status: 'planned',
    outcome: '',
    impact: '',
    action: '',
    quantity: '',
    remind_before_hours: 24,
  });
  
  // State for edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);

  // State for activity-detail popup (view outcome/impact/action)
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingActivity, setViewingActivity] = useState(null);

  // Check if an activity has any post-event notes filled
  const hasNotes = (a) => Boolean((a?.outcome && a.outcome.trim()) || (a?.impact && a.impact.trim()) || (a?.action && a.action.trim()));

  const openDetails = (activity) => {
    setViewingActivity(activity);
    setShowDetailsModal(true);
  };
  
  // Default activities data - ALWAYS empty for FIRA IND (no mock data)
  const getDefaultActivities = () => {
    if (isFiraInd) return [];
    return [
      { id: 1, title: 'Morning Live Trade Session', type: 'livetrade', date: '2026-01-08', startTime: '09:00', endTime: '11:00', description: 'XAUUSD & EURUSD analysis', status: 'completed', channel: 'public' },
      { id: 2, title: 'Gold Trading Webinar', type: 'webinar', date: '2026-01-08', startTime: '14:00', endTime: '16:00', description: 'Advanced gold trading strategies', status: 'planned', channel: 'public' },
      { id: 3, title: 'Content Shooting', type: 'shooting', date: '2026-01-09', startTime: '10:00', endTime: '12:00', description: 'Monthly performance video', status: 'planned', channel: 'public' },
      { id: 4, title: 'Evening Signal Session', type: 'signal', date: '2026-01-08', startTime: '20:00', endTime: '21:00', description: 'NY session signals', status: 'planned', channel: 'public' },
      { id: 5, title: 'VIP Live Trade', type: 'livetrade', date: '2026-01-08', startTime: '15:00', endTime: '17:00', description: 'Exclusive VIP session', status: 'completed', channel: 'vip' },
      { id: 6, title: 'Strategy Class', type: 'webinar', date: '2026-01-10', startTime: '11:00', endTime: '13:00', description: 'Scalping techniques', status: 'planned', channel: 'public' },
      { id: 7, title: 'Morning Analysis', type: 'livesession', date: '2026-01-11', startTime: '08:00', endTime: '09:00', description: 'Daily market overview', status: 'planned', channel: 'public' },
      { id: 8, title: 'Missed Session', type: 'livetrade', date: '2026-01-05', startTime: '10:00', endTime: '12:00', description: 'Cancelled due to technical issues', status: 'missed', channel: 'public' },
      { id: 9, title: 'VIP Webinar', type: 'webinar', date: '2026-01-12', startTime: '19:00', endTime: '21:00', description: 'Risk management masterclass', status: 'planned', channel: 'vip' },
      { id: 10, title: 'Content Creation', type: 'shooting', date: '2026-01-15', startTime: '14:00', endTime: '18:00', description: 'Instagram & TikTok content', status: 'planned', channel: 'public' },
    ];
  };
  
  // Load activities from MongoDB API
  const [activities, setActivities] = useState([]);
  const [activitiesLoaded, setActivitiesLoaded] = useState(false);

  // Sensors for drag-and-drop on the Daily Schedule list. Small activation
  // distance so clicks (open details) still work without triggering drag.
  const dailyDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  
  useEffect(() => {
    if (!ib?.id) return;
    setActivitiesLoaded(false);
    setActivities([]);
    const fetchActivities = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/ib-data/${ib.id}/activities`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.data?.activities) {
          setActivities(res.data.data.activities);
        }
        setActivitiesLoaded(true);
      } catch (e) { console.error('Failed to load activities:', e); }
    };
    fetchActivities();
  }, [ib?.id]);
  
  // Explicit save — only called after user actions
  const saveActivities = React.useCallback((data) => {
    if (!ib?.id) return;
    const token = localStorage.getItem('token');
    axios.put(`${API}/ib-data/${ib.id}/activities`, { data: { activities: data } }, { headers: { Authorization: `Bearer ${token}` } }).catch((e) => console.error('Save activities failed:', e));
  }, [ib?.id]);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateString = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getActivitiesForDate = (date) => {
    const dateStr = formatDateString(date);
    return activities.filter(a => a.date === dateStr && a.channel === channelType);
  };

  // Common sort: user-defined sort_order (via drag) takes precedence, then
  // startTime as tie-breaker. Used everywhere activities need consistent
  // ordering so calendar dots + upcoming list follow the drag-and-drop order.
  const sortByOrderThenTime = React.useCallback((a, b) => {
    const ao = Number.isFinite(a.sort_order) ? a.sort_order : 9999;
    const bo = Number.isFinite(b.sort_order) ? b.sort_order : 9999;
    if (ao !== bo) return ao - bo;
    return (a.startTime || '').localeCompare(b.startTime || '');
  }, []);

  const getActivitiesForSelectedDate = () => {
    let filtered = getActivitiesForDate(selectedDate);
    if (filterType !== 'all') {
      filtered = filtered.filter(a => a.type === filterType);
    }
    return filtered.sort(sortByOrderThenTime);
  };

  // Persist a reordering of the selected-date activities. Assigns sequential
  // sort_order to each entry so the order sticks across reloads. Non-visible
  // activities (other dates / channels) are preserved untouched.
  const handleReorderDailySchedule = (newOrder) => {
    // Build id-to-position map from the new order (0-based)
    const idToOrder = {};
    newOrder.forEach((a, idx) => { idToOrder[a.id] = idx; });
    const affectedIds = new Set(newOrder.map(a => a.id));
    const updated = activities.map(a => affectedIds.has(a.id) ? { ...a, sort_order: idToOrder[a.id] } : a);
    setActivities(updated);
    saveActivities(updated);
    toast.success('Activity order updated');
  };

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  // Create activity handler
  const handleCreateActivity = () => {
    if (!newActivity.title || !newActivity.date || !newActivity.startTime) {
      return;
    }
    const user = getCurrentUser();
    const activity = {
      id: Date.now(),
      title: newActivity.title,
      type: newActivity.type,
      date: newActivity.date,
      startTime: newActivity.startTime,
      endTime: newActivity.endTime || newActivity.startTime,
      description: newActivity.description,
      status: 'planned',
      channel: channelType,
      outcome: newActivity.outcome || '',
      impact: newActivity.impact || '',
      action: newActivity.action || '',
      // Quantity tracking — used for shooting_content / publish_content.
      // Stored as number (defaulting to 1) so the scorecard rollup can sum
      // values without parsing. Other types ignore this field.
      quantity: QUANTITY_TYPES.has(newActivity.type)
        ? (newActivity.quantity === '' || newActivity.quantity == null ? 1 : Number(newActivity.quantity))
        : 1,
      // Reminder — how many hours before start to trigger notification.
      // 0 = no reminder. Used by iCal VALARM and in-app reminders panel.
      remind_before_hours: Number(newActivity.remind_before_hours) || 0,
      // Audit fields
      created_by: user.id || '',
      created_by_name: user.name || user.email || 'Unknown',
      created_at: new Date().toISOString(),
      // Auto-approve all new activities (user preference: no PM gate required).
      approval_status: 'approved',
    };
    setActivities(prev => [...prev, activity]);
    saveActivities([...activities, activity]);
    setShowCreateModal(false);
    // Reset form
    setNewActivity({
      title: '',
      type: 'livetrade',
      date: '',
      startTime: '',
      endTime: '',
      description: '',
      status: 'planned',
      outcome: '',
      impact: '',
      action: '',
      quantity: '',
      remind_before_hours: 24,
    });
    // Select the date to show the new activity
    setSelectedDate(new Date(activity.date));
    toast.success('Activity created');
  };

  // Open edit modal
  const openEditModal = (activity) => {
    setEditingActivity({ ...activity });
    setShowEditModal(true);
  };

  // Handle edit activity
  const handleEditActivity = () => {
    if (!editingActivity || !editingActivity.title || !editingActivity.date || !editingActivity.startTime) return;
    const newList = activities.map(a => a.id === editingActivity.id ? editingActivity : a);
    setActivities(newList);
    saveActivities(newList);
    setShowEditModal(false);
    setEditingActivity(null);
  };

  // Update activity status (records completed_by when marked complete)
  const updateActivityStatus = (id, newStatus) => {
    const user = getCurrentUser();
    const now = new Date().toISOString();
    const newList = activities.map(a => {
      if (a.id !== id) return a;
      const updated = { ...a, status: newStatus };
      if (newStatus === 'completed') {
        updated.completed_by = user.id || '';
        updated.completed_by_name = user.name || user.email || 'Unknown';
        updated.completed_at = now;
      } else if (a.status === 'completed' && newStatus !== 'completed') {
        // Revoke credit if reverting completion
        delete updated.completed_by;
        delete updated.completed_by_name;
        delete updated.completed_at;
      }
      return updated;
    });
    setActivities(newList);
    saveActivities(newList);
    if (newStatus === 'completed') toast.success('Marked Done — credit recorded');
  };

  // Approve / Reject (PM workflow)
  const setApproval = (id, approval_status) => {
    const user = getCurrentUser();
    if (!isManagerRole(user)) { toast.error('Only Project Manager / Admin can approve/reject'); return; }
    const now = new Date().toISOString();
    const newList = activities.map(a => a.id === id ? {
      ...a,
      approval_status,
      approved_by: user.id || '',
      approved_by_name: user.name || user.email || 'Unknown',
      approved_at: now,
      // If rejected, also mark activity status as rejected
      ...(approval_status === 'rejected' ? { status: 'rejected' } : {}),
    } : a);
    setActivities(newList);
    saveActivities(newList);
    toast.success(approval_status === 'approved' ? 'Activity approved' : 'Activity rejected');
  };

  // Duplicate activity — supports modes: 'single' (default), 'week', 'month'.
  // - 'single': pre-fill Create modal with same data but empty date (user picks)
  // - 'week': clone the activity for the remaining days of the same ISO week
  //   (Mon–Sun) — skips the source date itself.
  // - 'month': clone the activity for every day in the source month
  //   starting from the day AFTER source date (so we don't duplicate the source).
  const duplicateActivity = (activity, mode = 'single') => {
    if (mode === 'single') {
      setNewActivity({
        title: activity.title || '',
        type: activity.type || 'livetrade',
        date: '',
        startTime: activity.startTime || '',
        endTime: activity.endTime || '',
        description: activity.description || '',
        status: 'planned',
        outcome: '',
        impact: '',
        action: '',
        quantity: activity.quantity ?? '',
      });
      setShowCreateModal(true);
      toast.info('Activity duplicated — adjust the date & submit');
      return;
    }

    const src = new Date(activity.date);
    if (Number.isNaN(src.getTime())) { toast.error('Source activity has invalid date'); return; }

    let targetDates = [];
    if (mode === 'week') {
      // ISO week — Mon=1 ... Sun=0/7
      const day = src.getDay() === 0 ? 7 : src.getDay();
      const monday = new Date(src);
      monday.setDate(src.getDate() - (day - 1));
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        if (d.toDateString() === src.toDateString()) continue;
        targetDates.push(d);
      }
    } else if (mode === 'month') {
      const y = src.getFullYear(); const m = src.getMonth();
      const lastDay = new Date(y, m + 1, 0).getDate();
      for (let d = src.getDate() + 1; d <= lastDay; d++) {
        targetDates.push(new Date(y, m, d));
      }
    }

    if (targetDates.length === 0) { toast.info('No additional days to duplicate to'); return; }

    const user = getCurrentUser();
    const now = new Date().toISOString();
    const clones = targetDates.map((d) => ({
      ...activity,
      id: Date.now() + Math.floor(Math.random() * 100000) + d.getTime(),
      date: d.toISOString().slice(0, 10),
      status: 'planned',
      // Clones inherit approved state — no extra PM gate
      approval_status: 'approved',
      completed_by: undefined,
      completed_by_name: undefined,
      completed_at: undefined,
      approved_by: undefined,
      approved_by_name: undefined,
      approved_at: undefined,
      created_by: user.id || '',
      created_by_name: user.name || user.email || 'Unknown',
      created_at: now,
    }));
    const newList = [...activities, ...clones];
    setActivities(newList);
    saveActivities(newList);
    toast.success(`Duplicated to ${clones.length} ${mode === 'week' ? 'days this week' : 'days this month'}`);
  };

  // Bulk: copy ALL activities in the currently viewed month → another month.
  // Preserves day-of-month, time, type, channel, description. Status resets to
  // 'planned'. Skips days that don't exist in the target month (e.g. Jan 31 → Feb).
  const runCopyMonth = () => {
    if (!copyMonthTarget) { toast.error('Pilih target month dulu'); return; }
    const [ty, tm] = copyMonthTarget.split('-').map(Number);
    if (!ty || !tm) { toast.error('Target month tak sah'); return; }
    const srcY = currentDate.getFullYear();
    const srcM = currentDate.getMonth();
    if (ty === srcY && (tm - 1) === srcM) { toast.error('Target tak boleh sama dengan source month'); return; }

    const srcActivities = activities.filter((a) => {
      if (!a || a.channel !== channelType) return false;
      const d = new Date(a.date);
      if (Number.isNaN(d.getTime())) return false;
      if (d.getFullYear() !== srcY || d.getMonth() !== srcM) return false;
      if (!copyMonthIncludeCompleted && a.status !== 'planned') return false;
      return true;
    });
    if (srcActivities.length === 0) { toast.info('Tiada aktiviti untuk di-copy dari bulan ni'); return; }

    const lastDayTarget = new Date(ty, tm, 0).getDate();
    const user = getCurrentUser();
    const now = new Date().toISOString();
    const clones = [];
    let skipped = 0;
    for (const a of srcActivities) {
      const srcDate = new Date(a.date);
      const day = srcDate.getDate();
      if (day > lastDayTarget) { skipped++; continue; }
      const newDate = new Date(ty, tm - 1, day);
      clones.push({
        ...a,
        id: Date.now() + Math.floor(Math.random() * 1000000) + day,
        date: newDate.toISOString().slice(0, 10),
        status: 'planned',
        approval_status: 'approved',
        completed_by: undefined,
        completed_by_name: undefined,
        completed_at: undefined,
        approved_by: undefined,
        approved_by_name: undefined,
        approved_at: undefined,
        created_by: user.id || '',
        created_by_name: user.name || user.email || 'Unknown',
        created_at: now,
      });
    }
    const newList = [...activities, ...clones];
    setActivities(newList);
    saveActivities(newList);
    const monthLabel = new Date(ty, tm - 1, 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' });
    toast.success(`Copied ${clones.length} activities → ${monthLabel}${skipped ? ` (${skipped} dilangkau — hari tak wujud)` : ''}`);
    setShowCopyMonthModal(false);
    setCopyMonthTarget('');
  };

  // Delete activity
  const deleteActivity = (id) => {
    const newList = activities.filter(a => a.id !== id);
    setActivities(newList);
    saveActivities(newList);
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    
    // Empty cells for days before first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, date: null });
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayActivities = activities
        .filter(a => a.date === formatDateString(date) && a.channel === channelType)
        .sort(sortByOrderThenTime);
      days.push({ day, date, activities: dayActivities });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();
  const selectedDateActivities = getActivitiesForSelectedDate();
  const isToday = (date) => date && formatDateString(date) === formatDateString(new Date());
  const isSelected = (date) => date && formatDateString(date) === formatDateString(selectedDate);

  // Stats for the month
  const monthActivities = activities.filter(a => {
    const actDate = new Date(a.date);
    return actDate.getMonth() === currentDate.getMonth() && 
           actDate.getFullYear() === currentDate.getFullYear() &&
           a.channel === channelType;
  });
  
  const plannedCount = monthActivities.filter(a => a.status === 'planned').length;
  const completedCount = monthActivities.filter(a => a.status === 'completed').length;
  const missedCount = monthActivities.filter(a => a.status === 'missed').length;

  return (
    <>
      {/* Sticky Header */}
      <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold font-heading tracking-tight">Channel Activity</h1>
            <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">Activity Schedule & Calendar</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Segmented Channel Toggle — PUBLIC vs VIP (Option B per UX feedback) */}
            <div className="inline-flex p-1 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))]" data-testid="channel-segmented-toggle">
              {(['public', 'vip']).map((key) => {
                const theme = channelTheme[key];
                const ThemeIcon = theme.icon;
                const isActive = channelType === key;
                return (
                  <button
                    key={key}
                    onClick={() => setChannelType(key)}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                      isActive
                        ? `text-white ${theme.shadow}`
                        : 'text-[hsl(var(--text-muted))] hover:text-white'
                    }`}
                    style={isActive ? { background: `linear-gradient(135deg, ${theme.color}40, ${theme.color}20)`, border: `1px solid ${theme.color}80` } : {}}
                    data-testid={`channel-toggle-${key}`}
                  >
                    <ThemeIcon size={14} style={{ color: isActive ? theme.color : undefined }} />
                    {theme.label}
                  </button>
                );
              })}
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSummaryReport(true)}
              className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 text-xs"
              data-testid="summary-report-btn"
              title="Tunjuk chart + statistic untuk semua aktiviti bulan ni"
            >
              <BarChart3 size={14} className="mr-1" />
              <span className="hidden sm:inline">Summary Report</span>
              <span className="sm:hidden">Report</span>
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // Default target = next month
                const nm = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
                setCopyMonthTarget(`${nm.getFullYear()}-${String(nm.getMonth() + 1).padStart(2, '0')}`);
                setShowCopyMonthModal(true);
              }}
              className="border-[#8B5CF6]/40 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 text-xs"
              data-testid="copy-month-btn"
              title="Copy semua aktiviti dari bulan ini ke bulan lain"
            >
              <Copy size={14} className="mr-1" />
              <span className="hidden sm:inline">Copy Month</span>
              <span className="sm:hidden">Copy</span>
            </Button>

            <Button
              size="sm"
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-[#E63946] to-[#F97316] hover:from-[#E63946]/90 hover:to-[#F97316]/90 text-white text-xs"
              data-testid="create-activity-btn"
            >
              <Plus size={14} className="mr-1" />
              <span className="hidden sm:inline">Create Activity</span>
              <span className="sm:hidden">Create</span>
            </Button>
          </div>
        </div>

        {/* Channel banner indicator — large coloured strip so user always knows which channel they're in */}
        <div
          className={`px-4 sm:px-8 py-2 flex items-center gap-2 border-t bg-gradient-to-r ${channelTheme[channelType].accent} ${channelTheme[channelType].border}`}
          data-testid="channel-banner"
        >
          {React.createElement(channelTheme[channelType].icon, { size: 16, style: { color: channelTheme[channelType].color } })}
          <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: channelTheme[channelType].color }}>
            {ib?.name || 'CHANNEL'} • {channelTheme[channelType].label} CHANNEL
          </span>
          <span className="text-[10px] text-[hsl(var(--text-muted))] ml-auto">All entries below belong to <span className="font-bold" style={{ color: channelTheme[channelType].color }}>{channelTheme[channelType].label}</span></span>
        </div>
      </header>

      {/* Scrollable Content */}
      <div className="overflow-x-hidden">
        <div className="px-4 sm:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6 w-full">
          
          {/* MONTHLY OVERVIEW - Stats with Section Header */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#8B5CF6]/10 via-transparent to-[#3B82F6]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={BarChart3} title="Monthly Overview" subtitle={`${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()} Statistics`} />
              
              <div className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Total Activities</p>
                    <p className="text-2xl font-mono font-bold text-white">{monthActivities.length}</p>
                  </div>
                  <div className="bg-[hsl(var(--surface-elevated))] border border-[#3B82F6]/30 rounded-xl p-4">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Planned</p>
                    <p className="text-2xl font-mono font-bold text-[#3B82F6]">{plannedCount}</p>
                  </div>
                  <div className="bg-[hsl(var(--surface-elevated))] border border-[#10B981]/30 rounded-xl p-4">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Completed</p>
                    <p className="text-2xl font-mono font-bold text-[#10B981]">{completedCount}</p>
                  </div>
                  <div className="bg-[hsl(var(--surface-elevated))] border border-[#E63946]/30 rounded-xl p-4">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Missed</p>
                    <p className="text-2xl font-mono font-bold text-[#E63946]">{missedCount}</p>
                  </div>
                  <div className="bg-[hsl(var(--surface-elevated))] border border-[#F59E0B]/30 rounded-xl p-4">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Completion Rate</p>
                    <p className="text-2xl font-mono font-bold text-[#F59E0B]">
                      {completedCount + missedCount > 0 
                        ? `${Math.round((completedCount / (completedCount + missedCount)) * 100)}%`
                        : '-'
                      }
                    </p>
                  </div>
                </div>
                
                {/* Activity Type Breakdown */}
                <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
                  <p className="text-xs text-[#6B7280] mb-3">Activity Breakdown</p>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(activityTypes).map(([key, config]) => {
                      const count = monthActivities.filter(a => a.type === key).length;
                      if (count === 0) return null;
                      return (
                        <div key={key} className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${config.color}15` }}>
                          {React.createElement(config.icon, { size: 14, style: { color: config.color } })}
                          <span className="text-xs font-medium" style={{ color: config.color }}>{count} {config.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar & Daily Schedule - Same height */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Monthly Calendar */}
            <div className="col-span-2 relative group/section h-[620px]">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden h-full flex flex-col">
                <SectionHeader icon={CalendarIcon} title="Monthly Calendar" subtitle={`${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`} />
                
                <div className="p-4 flex-1 flex flex-col">
                  {/* Calendar Navigation */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={goToPreviousMonth} className="h-8 w-8 p-0 border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--surface-elevated))]">
                        <ChevronLeft size={16} />
                      </Button>
                      <span className="text-base font-semibold text-white min-w-[160px] text-center">
                        {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                      </span>
                      <Button variant="outline" size="sm" onClick={goToNextMonth} className="h-8 w-8 p-0 border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--surface-elevated))]">
                        <ChevronRight size={16} />
                      </Button>
                    </div>
                    <Button variant="outline" size="sm" onClick={goToToday} className="h-8 text-xs border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--surface-elevated))]">
                      Today
                    </Button>
                  </div>
                  
                  {/* Days of Week Header */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {daysOfWeek.map(day => (
                      <div key={day} className="text-center text-xs font-semibold text-[#6B7280] py-1">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Grid - Stretch to fill */}
                  <div className="grid grid-cols-7 gap-1 flex-1">
                    {calendarDays.map((dayData, idx) => (
                      <div
                        key={idx}
                        onClick={() => dayData.date && setSelectedDate(dayData.date)}
                        className={`
                          p-2 rounded-lg border cursor-pointer transition-all flex flex-col
                          ${!dayData.day ? 'bg-transparent border-transparent' : 'bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] hover:border-[#E63946]/50'}
                          ${isSelected(dayData.date) ? 'ring-2 ring-[#E63946] border-[#E63946]' : ''}
                          ${isToday(dayData.date) ? 'bg-[#E63946]/10' : ''}
                        `}
                      >
                        {dayData.day && (
                          <>
                            <p className={`text-sm font-semibold ${isToday(dayData.date) ? 'text-[#E63946]' : 'text-white'}`}>
                              {dayData.day}
                            </p>
                            {/* Activity indicators - max 6 dots in 2 rows */}
                            <div className="flex flex-wrap gap-0.5 mt-1">
                              {dayData.activities?.slice(0, 6).map((act, i) => (
                                <div
                                  key={i}
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: activityTypes[act.type]?.color }}
                                  title={act.title}
                                />
                              ))}
                              {dayData.activities?.length > 6 && (
                                <span className="text-[8px] text-[#6B7280]">+{dayData.activities.length - 6}</span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Activity Type Legend - Fixed at bottom */}
                <div className="px-4 pb-3 pt-2 border-t border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(activityTypes).map(([key, config]) => (
                      <div key={key} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                        <span className="text-xs text-[#9CA3AF]">{config.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Schedule - Same height as Calendar */}
            <div className="relative group/section h-[620px]">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#3B82F6]/10 via-transparent to-[#10B981]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden h-full flex flex-col">
                <SectionHeader 
                  icon={Clock} 
                  title="Daily Schedule" 
                  subtitle={selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })} 
                />
                
                <div className="p-4 flex-1 flex flex-col overflow-hidden">
                  {/* Filter */}
                  <div className="mb-4">
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-full bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white text-sm">
                        <SelectValue placeholder="Filter by type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                        <SelectItem value="all" className="text-white">All Activities</SelectItem>
                        {Object.entries(activityTypes).map(([key, config]) => (
                          <SelectItem key={key} value={key} className="text-white">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                              {config.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Activities List - Scroll within box */}
                  <div className="space-y-3 flex-1 overflow-y-auto pr-1">
                    {selectedDateActivities.length === 0 ? (
                      <div className="text-center py-8">
                        <CalendarIcon size={32} className="mx-auto text-[#6B7280] mb-2" />
                        <p className="text-sm text-[#6B7280]">No activities scheduled</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 border-[hsl(var(--border))] text-white"
                          onClick={() => {
                            setNewActivity({ ...newActivity, date: formatDateString(selectedDate) });
                            setShowCreateModal(true);
                          }}
                        >
                          <Plus size={14} className="mr-1" />
                          Add Activity
                        </Button>
                      </div>
                    ) : (
                      <DndContext
                        sensors={dailyDndSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => {
                          const { active, over } = event;
                          if (!over || active.id === over.id) return;
                          const oldIdx = selectedDateActivities.findIndex(a => a.id === active.id);
                          const newIdx = selectedDateActivities.findIndex(a => a.id === over.id);
                          if (oldIdx === -1 || newIdx === -1) return;
                          const newOrder = arrayMove(selectedDateActivities, oldIdx, newIdx);
                          handleReorderDailySchedule(newOrder);
                        }}
                      >
                        <SortableContext items={selectedDateActivities.map(a => a.id)} strategy={verticalListSortingStrategy}>
                      {selectedDateActivities.map((activity) => {
                        const typeConfig = activityTypes[activity.type];
                        const StatusIcon = statusConfig[activity.status]?.icon || Clock;
                        
                        return (
                          <SortableActivityCard key={activity.id} id={activity.id}>
                          <div
                            className="p-3 rounded-lg border transition-all cursor-pointer hover:brightness-110"
                            style={{ 
                              backgroundColor: typeConfig.bgColor,
                              borderColor: `${typeConfig.color}30`
                            }}
                            onClick={() => openDetails(activity)}
                            data-testid={`activity-card-${activity.id}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                                  style={{ backgroundColor: `${typeConfig.color}30` }}
                                >
                                  {React.createElement(typeConfig.icon, { size: 16, style: { color: typeConfig.color } })}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                                    {activity.title}
                                    {hasNotes(activity) && <span title="Outcome/Impact/Action noted" className="inline-flex items-center gap-0.5 text-[9px] font-bold text-[#10B981] bg-[#10B981]/15 border border-[#10B981]/30 rounded px-1 py-0.5"><StickyNote size={8} />noted</span>}
                                  </p>
                                  <p className="text-xs text-[#9CA3AF]">{activity.startTime} - {activity.endTime}</p>
                                </div>
                              </div>
                              <Badge 
                                className="text-[10px]"
                                style={{ 
                                  backgroundColor: `${statusConfig[activity.status].color}20`,
                                  color: statusConfig[activity.status].color,
                                  borderColor: `${statusConfig[activity.status].color}50`
                                }}
                              >
                                <StatusIcon size={10} className="mr-1" />
                                {statusConfig[activity.status].label}
                              </Badge>
                            </div>
                            
                            {/* Pending approval indicator */}
                            {activity.approval_status && activity.approval_status !== 'approved' && (
                              <div className="mb-2 -mt-1">
                                <span className="inline-flex items-center gap-1 text-[9px] font-bold rounded px-1.5 py-0.5"
                                  style={{ backgroundColor: `${approvalConfig[activity.approval_status]?.color || '#F59E0B'}20`, color: approvalConfig[activity.approval_status]?.color || '#F59E0B', border: `1px solid ${approvalConfig[activity.approval_status]?.color || '#F59E0B'}40` }}>
                                  {approvalConfig[activity.approval_status]?.label}
                                </span>
                              </div>
                            )}

                            {activity.description && (
                              <p className="text-xs text-[#9CA3AF] mb-2">{activity.description}</p>
                            )}

                            {/* PM Approve / Reject (only if pending and user is admin/manager) */}
                            {activity.approval_status === 'pending' && isManagerRole(getCurrentUser()) && (
                              <div className="flex items-center gap-1 mb-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-[9px] text-[#10B981] hover:bg-[#10B981]/20"
                                  onClick={(e) => { e.stopPropagation(); setApproval(activity.id, 'approved'); }}
                                  data-testid={`approve-activity-${activity.id}`}
                                >
                                  <ThumbsUp size={10} className="mr-0.5" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-[9px] text-[#E63946] hover:bg-[#E63946]/20"
                                  onClick={(e) => { e.stopPropagation(); setApproval(activity.id, 'rejected'); }}
                                  data-testid={`reject-activity-${activity.id}`}
                                >
                                  <ThumbsDown size={10} className="mr-0.5" />
                                  Reject
                                </Button>
                              </div>
                            )}

                            {/* Action Buttons — Done/Miss only if approved (or no approval workflow) and still planned */}
                            {activity.status === 'planned' && (activity.approval_status !== 'pending' && activity.approval_status !== 'rejected') && (
                              <div className="flex items-center gap-1 pt-2 border-t flex-wrap" style={{ borderColor: `${typeConfig.color}20` }}>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-[9px] text-[#10B981] hover:bg-[#10B981]/20"
                                  onClick={(e) => { e.stopPropagation(); updateActivityStatus(activity.id, 'completed'); }}
                                  data-testid={`complete-activity-${activity.id}`}
                                  title="Done — credits this activity to you"
                                >
                                  <Check size={10} className="mr-0.5" />
                                  Done
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-[9px] text-[#E63946] hover:bg-[#E63946]/20"
                                  onClick={(e) => { e.stopPropagation(); updateActivityStatus(activity.id, 'missed'); }}
                                  data-testid={`missed-activity-${activity.id}`}
                                >
                                  <X size={10} className="mr-0.5" />
                                  Miss
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-[9px] text-[#3B82F6] hover:bg-[#3B82F6]/20"
                                  onClick={(e) => { e.stopPropagation(); openEditModal(activity); }}
                                  data-testid={`edit-activity-${activity.id}`}
                                >
                                  <Edit2 size={10} className="mr-0.5" />
                                  Edit
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-[9px] text-[#8B5CF6] hover:bg-[#8B5CF6]/20"
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid={`duplicate-activity-${activity.id}`}
                                      title="Duplicate — pick mode (single / week / month)"
                                    >
                                      <Copy size={10} className="mr-0.5" />
                                      Duplicate
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenuItem onClick={() => duplicateActivity(activity, 'single')} data-testid={`duplicate-single-${activity.id}`}>
                                      <Copy size={12} className="mr-2 text-[#8B5CF6]" /> Single date (manual)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => duplicateActivity(activity, 'week')} data-testid={`duplicate-week-${activity.id}`}>
                                      <CalendarIcon size={12} className="mr-2 text-[#3B82F6]" /> Whole week (Mon–Sun)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => duplicateActivity(activity, 'month')} data-testid={`duplicate-month-${activity.id}`}>
                                      <CalendarIcon size={12} className="mr-2 text-[#F59E0B]" /> Whole month (remaining days)
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-[#6B7280] hover:bg-[#6B7280]/20 hover:text-[#E63946] ml-auto"
                                  onClick={(e) => { e.stopPropagation(); deleteActivity(activity.id); }}
                                  data-testid={`delete-activity-${activity.id}`}
                                >
                                  <Trash2 size={10} />
                                </Button>
                              </div>
                            )}
                          </div>
                          </SortableActivityCard>
                        );
                      })}
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reminders & Calendar Sync (Item #3) — collapsible panel with guide */}
          {ib?.id && <ActivityRemindersPanel ibId={ib.id} />}

          {/* Upcoming Activities */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#F59E0B]/10 via-transparent to-[#8B5CF6]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <div className="flex items-center justify-between">
                <SectionHeader icon={AlertCircle} title="Upcoming Activities" subtitle="Next 7 days schedule" />
                {/* View toggle — grid vs list */}
                <div className="pr-6 pt-4 flex items-center gap-1 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg p-0.5 mr-0"
                  style={{ position: 'absolute', right: 24, top: 20 }}>
                  <button
                    onClick={() => { setUpcomingView('grid'); try { localStorage.setItem('activity_upcoming_view', 'grid'); } catch (_e) { /* localStorage disabled */ } }}
                    data-testid="upcoming-view-grid"
                    className={`px-2 py-1 rounded flex items-center gap-1 text-xs transition ${upcomingView === 'grid' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'text-[#9CA3AF] hover:text-white'}`}
                    title="Grid view"
                  ><LayoutGrid size={13} /> Grid</button>
                  <button
                    onClick={() => { setUpcomingView('list'); try { localStorage.setItem('activity_upcoming_view', 'list'); } catch (_e) { /* localStorage disabled */ } }}
                    data-testid="upcoming-view-list"
                    className={`px-2 py-1 rounded flex items-center gap-1 text-xs transition ${upcomingView === 'list' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'text-[#9CA3AF] hover:text-white'}`}
                    title="List view"
                  ><List size={13} /> List</button>
                </div>
              </div>
              
              <div className="p-6">
                {(() => {
                  const today = new Date();
                  const upcoming = activities
                    .filter(a => {
                      const actDate = new Date(a.date);
                      const diffDays = Math.ceil((actDate - today) / (1000 * 60 * 60 * 24));
                      return diffDays >= 0 && diffDays <= 7 && a.channel === channelType && a.status === 'planned';
                    })
                    // Group by date first, then use sort_order (drag-and-drop
                    // order) within each date so upcoming ordering matches
                    // what admin arranged in the Daily Schedule.
                    .sort((a, b) => {
                      const dateCmp = (a.date || '').localeCompare(b.date || '');
                      if (dateCmp !== 0) return dateCmp;
                      return sortByOrderThenTime(a, b);
                    });

                  if (upcoming.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <CalendarIcon size={32} className="mx-auto text-[#6B7280] mb-2" />
                        <p className="text-sm text-[#6B7280]">No upcoming activities in the next 7 days</p>
                      </div>
                    );
                  }

                  if (upcomingView === 'list') {
                    // Compact list view — shows ALL upcoming (not just 8) grouped visually by date
                    let lastDate = null;
                    return (
                      <div className="space-y-1" data-testid="upcoming-list-view">
                        {upcoming.map(activity => {
                          const typeConfig = activityTypes[activity.type];
                          const actDate = new Date(activity.date);
                          const dateKey = activity.date;
                          const showDateHeader = dateKey !== lastDate;
                          lastDate = dateKey;
                          return (
                            <React.Fragment key={activity.id}>
                              {showDateHeader && (
                                <div className="flex items-center gap-2 pt-2 pb-1 border-b border-[hsl(var(--border))]/50 first:pt-0">
                                  <CalendarIcon size={11} className="text-[#F59E0B]" />
                                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#F59E0B]">
                                    {actDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
                                  </p>
                                </div>
                              )}
                              <div
                                key={activity.id}
                                onClick={() => openDetails(activity)}
                                data-testid={`upcoming-list-row-${activity.id}`}
                                className="group/row flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-[hsl(var(--surface-elevated))] transition-colors border border-transparent hover:border-[hsl(var(--border))]"
                              >
                                <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: typeConfig.color }} />
                                <div className="text-xs text-[#9CA3AF] tabular-nums font-mono w-24 flex-shrink-0">{activity.startTime} - {activity.endTime}</div>
                                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                  <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${typeConfig.color}20` }}>
                                    {React.createElement(typeConfig.icon, { size: 12, style: { color: typeConfig.color } })}
                                  </div>
                                  <p className="text-sm font-medium text-white truncate">{activity.title}</p>
                                  {hasNotes(activity) && <span title="Notes added" className="flex-shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold text-[#10B981] bg-[#10B981]/15 border border-[#10B981]/30 rounded px-1"><StickyNote size={8} />noted</span>}
                                </div>
                                <Badge className="text-[9px] flex-shrink-0" style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}>{typeConfig.label}</Badge>
                                <div className="flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity flex-shrink-0">
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-[#3B82F6] hover:bg-[#3B82F6]/20"
                                    onClick={(e) => { e.stopPropagation(); openEditModal(activity); }}
                                    data-testid={`edit-upcoming-list-${activity.id}`}
                                  ><Edit2 size={11} /></Button>
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-[#E63946] hover:bg-[#E63946]/20"
                                    onClick={(e) => { e.stopPropagation(); deleteActivity(activity.id); }}
                                    data-testid={`delete-upcoming-list-${activity.id}`}
                                  ><Trash2 size={11} /></Button>
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    );
                  }

                  // Default: grid view (existing behaviour) — limits to 8 cards
                  return (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="upcoming-grid-view">
                      {upcoming.slice(0, 8).map((activity) => {
                    const typeConfig = activityTypes[activity.type];
                    const actDate = new Date(activity.date);
                    
                    return (
                      <div
                        key={activity.id}
                        className="p-4 rounded-xl border bg-[hsl(var(--surface-elevated))] group/card cursor-pointer hover:brightness-110 transition-all"
                        style={{ borderColor: `${typeConfig.color}30` }}
                        onClick={() => openDetails(activity)}
                        data-testid={`upcoming-card-${activity.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${typeConfig.color}20` }}
                            >
                              {React.createElement(typeConfig.icon, { size: 16, style: { color: typeConfig.color } })}
                            </div>
                            <Badge 
                              className="text-[10px]"
                              style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}
                            >
                              {typeConfig.label}
                            </Badge>
                            {hasNotes(activity) && <span title="Notes added" className="inline-flex items-center gap-0.5 text-[9px] font-bold text-[#10B981] bg-[#10B981]/15 border border-[#10B981]/30 rounded px-1 py-0.5"><StickyNote size={8} />noted</span>}
                          </div>
                          {/* Edit & Delete buttons */}
                          <div className="flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-[#3B82F6] hover:bg-[#3B82F6]/20"
                              onClick={(e) => { e.stopPropagation(); openEditModal(activity); }}
                              data-testid={`edit-upcoming-${activity.id}`}
                            >
                              <Edit2 size={12} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-[#E63946] hover:bg-[#E63946]/20"
                              onClick={(e) => { e.stopPropagation(); deleteActivity(activity.id); }}
                              data-testid={`delete-upcoming-${activity.id}`}
                            >
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm font-semibold text-white mb-1 truncate">{activity.title}</p>
                        <p className="text-xs text-[#9CA3AF]">
                          {actDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </p>
                        <p className="text-xs text-[#6B7280]">{activity.startTime} - {activity.endTime}</p>
                      </div>
                    );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Activity Status Breakdown - Pending, Completed, Missed */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#3B82F6]/10 via-transparent to-[#10B981]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={ListChecks} title="Activity Status Breakdown" subtitle="View activities by status" />
              
              <div className="p-6">
                <Tabs defaultValue="pending" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 bg-[hsl(var(--surface-elevated))] mb-4">
                    <TabsTrigger 
                      value="pending" 
                      className="data-[state=active]:bg-[#3B82F6]/20 data-[state=active]:text-[#3B82F6]"
                    >
                      <Clock size={14} className="mr-2" />
                      Pending ({monthActivities.filter(a => a.status === 'planned').length})
                    </TabsTrigger>
                    <TabsTrigger 
                      value="completed"
                      className="data-[state=active]:bg-[#10B981]/20 data-[state=active]:text-[#10B981]"
                    >
                      <Check size={14} className="mr-2" />
                      Completed ({monthActivities.filter(a => a.status === 'completed').length})
                    </TabsTrigger>
                    <TabsTrigger 
                      value="missed"
                      className="data-[state=active]:bg-[#E63946]/20 data-[state=active]:text-[#E63946]"
                    >
                      <XCircle size={14} className="mr-2" />
                      Missed ({monthActivities.filter(a => a.status === 'missed').length})
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Pending Activities */}
                  <TabsContent value="pending" className="mt-0">
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {monthActivities.filter(a => a.status === 'planned').length === 0 ? (
                        <div className="text-center py-8">
                          <Clock size={32} className="mx-auto text-[#6B7280] mb-2" />
                          <p className="text-sm text-[#6B7280]">No pending activities this month</p>
                        </div>
                      ) : (
                        monthActivities
                          .filter(a => a.status === 'planned')
                          .sort((a, b) => {
                            const dateCmp = (a.date || '').localeCompare(b.date || '');
                            if (dateCmp !== 0) return dateCmp;
                            return sortByOrderThenTime(a, b);
                          })
                          .map((activity) => {
                            const typeConfig = activityTypes[activity.type];
                            const actDate = new Date(activity.date);
                            return (
                              <div
                                key={activity.id}
                                className="flex items-center justify-between p-3 rounded-lg border bg-[hsl(var(--surface-elevated))] cursor-pointer hover:brightness-110 transition-all"
                                style={{ borderColor: `${typeConfig.color}30` }}
                                onClick={() => openDetails(activity)}
                                data-testid={`breakdown-pending-${activity.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div 
                                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: `${typeConfig.color}20` }}
                                  >
                                    {React.createElement(typeConfig.icon, { size: 14, style: { color: typeConfig.color } })}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-white flex items-center gap-1.5">
                                      {activity.title}
                                      {hasNotes(activity) && <span title="Notes added" className="inline-flex items-center gap-0.5 text-[8px] font-bold text-[#10B981] bg-[#10B981]/15 border border-[#10B981]/30 rounded px-1 py-0.5"><StickyNote size={7} />noted</span>}
                                    </p>
                                    <p className="text-xs text-[#6B7280]">
                                      {actDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} • {activity.startTime}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-[#3B82F6] hover:bg-[#3B82F6]/20"
                                    onClick={(e) => { e.stopPropagation(); openEditModal(activity); }}
                                  >
                                    <Edit2 size={12} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-[#6B7280] hover:bg-[#E63946]/20 hover:text-[#E63946]"
                                    onClick={(e) => { e.stopPropagation(); deleteActivity(activity.id); }}
                                  >
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                      )}
                    </div>
                  </TabsContent>
                  
                  {/* Completed Activities - No Edit/Delete buttons */}
                  <TabsContent value="completed" className="mt-0">
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {monthActivities.filter(a => a.status === 'completed').length === 0 ? (
                        <div className="text-center py-8">
                          <Check size={32} className="mx-auto text-[#6B7280] mb-2" />
                          <p className="text-sm text-[#6B7280]">No completed activities this month</p>
                        </div>
                      ) : (
                        monthActivities
                          .filter(a => a.status === 'completed')
                          .sort((a, b) => b.date.localeCompare(a.date) || b.startTime.localeCompare(a.startTime))
                          .map((activity) => {
                            const typeConfig = activityTypes[activity.type];
                            const actDate = new Date(activity.date);
                            return (
                              <div
                                key={activity.id}
                                className="flex items-center p-3 rounded-lg border bg-[#10B981]/5 cursor-pointer hover:brightness-110 transition-all"
                                style={{ borderColor: '#10B98130' }}
                                onClick={() => openDetails(activity)}
                                data-testid={`breakdown-completed-${activity.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#10B981]/20">
                                    <Check size={14} className="text-[#10B981]" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-white flex items-center gap-1.5">
                                      {activity.title}
                                      {hasNotes(activity) && <span title="Notes added" className="inline-flex items-center gap-0.5 text-[8px] font-bold text-[#10B981] bg-[#10B981]/15 border border-[#10B981]/30 rounded px-1 py-0.5"><StickyNote size={7} />noted</span>}
                                    </p>
                                    <p className="text-xs text-[#6B7280]">
                                      {actDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} • {activity.startTime} • 
                                      <span className="text-[#10B981] ml-1">{typeConfig.label}</span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                      )}
                    </div>
                  </TabsContent>
                  
                  {/* Missed Activities - No Edit/Delete buttons */}
                  <TabsContent value="missed" className="mt-0">
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {monthActivities.filter(a => a.status === 'missed').length === 0 ? (
                        <div className="text-center py-8">
                          <XCircle size={32} className="mx-auto text-[#6B7280] mb-2" />
                          <p className="text-sm text-[#6B7280]">No missed activities this month</p>
                        </div>
                      ) : (
                        monthActivities
                          .filter(a => a.status === 'missed')
                          .sort((a, b) => b.date.localeCompare(a.date) || b.startTime.localeCompare(a.startTime))
                          .map((activity) => {
                            const typeConfig = activityTypes[activity.type];
                            const actDate = new Date(activity.date);
                            return (
                              <div
                                key={activity.id}
                                className="flex items-center p-3 rounded-lg border bg-[#E63946]/5 cursor-pointer hover:brightness-110 transition-all"
                                style={{ borderColor: '#E6394630' }}
                                onClick={() => openDetails(activity)}
                                data-testid={`breakdown-missed-${activity.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#E63946]/20">
                                    <XCircle size={14} className="text-[#E63946]" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-white flex items-center gap-1.5">
                                      {activity.title}
                                      {hasNotes(activity) && <span title="Notes added" className="inline-flex items-center gap-0.5 text-[8px] font-bold text-[#10B981] bg-[#10B981]/15 border border-[#10B981]/30 rounded px-1 py-0.5"><StickyNote size={7} />noted</span>}
                                    </p>
                                    <p className="text-xs text-[#6B7280]">
                                      {actDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} • {activity.startTime} • 
                                      <span className="text-[#E63946] ml-1">{typeConfig.label}</span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Create Activity Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus size={20} className="text-[#E63946]" />
              Create New Activity
            </DialogTitle>
            {/* Channel context banner — so user always knows whether they are adding to Public or VIP */}
            <div
              className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-md border bg-gradient-to-r ${channelTheme[channelType].accent} ${channelTheme[channelType].border}`}
              data-testid="create-activity-channel-banner"
            >
              {React.createElement(channelTheme[channelType].icon, { size: 14, style: { color: channelTheme[channelType].color } })}
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: channelTheme[channelType].color }}>
                Adding to: {ib?.name || 'Channel'} • {channelTheme[channelType].label}
              </span>
              <span className="ml-auto text-[9px] text-[hsl(var(--text-muted))]">
                Wrong channel? Close and switch the toggle in the header.
              </span>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#9CA3AF] text-xs">Activity Title *</Label>
              <Input
                value={newActivity.title}
                onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                placeholder="e.g., Morning Live Trade Session"
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 placeholder:text-[#6B7280]"
                data-testid="create-activity-title"
              />
            </div>
            
            <div>
              <Label className="text-[#9CA3AF] text-xs">Activity Type *</Label>
              <Select value={newActivity.type} onValueChange={(v) => setNewActivity({ ...newActivity, type: v })}>
                <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" data-testid="create-activity-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                  {Object.entries(activityTypes).filter(([key]) => key !== 'shooting').map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-white">
                      <div className="flex items-center gap-2">
                        {React.createElement(config.icon, { size: 14, style: { color: config.color } })}
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quantity — only relevant for content production / publishing types.
                Shooting Content quantity feeds the channel scorecard's
                Total Video (Organic) metric (VID-ORG). Publish Content is
                tracked here for awareness but NOT counted into the scorecard. */}
            {QUANTITY_TYPES.has(newActivity.type) && (
              <div>
                <Label className="text-[#9CA3AF] text-xs">
                  Quantity <span className="text-[#6B7280] font-normal">(optional)</span>
                </Label>
                <Input
                  type="number"
                  min="0"
                  max="999"
                  value={newActivity.quantity ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0);
                    setNewActivity({ ...newActivity, quantity: v });
                  }}
                  placeholder={newActivity.type === 'publish_content' ? 'e.g. 3 posts published' : 'e.g. 5 videos shot'}
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"
                  data-testid="create-activity-quantity"
                />
                <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">
                  {newActivity.type === 'publish_content'
                    ? 'Tracked for awareness — bukan untuk scorecard.'
                    : <>Counted into <span className="text-amber-300">Total Video (Organic)</span> scorecard apabila status = Done.</>}
                </p>
              </div>
            )}
            
            <div>
              <Label className="text-[#9CA3AF] text-xs">Date *</Label>
              <Input
                type="date"
                value={newActivity.date}
                onChange={(e) => {
                  console.log('Date changed:', e.target.value);
                  setNewActivity({ ...newActivity, date: e.target.value });
                }}
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                data-testid="create-activity-date"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#9CA3AF] text-xs">Start Time *</Label>
                <Input
                  type="time"
                  value={newActivity.startTime}
                  onChange={(e) => {
                    console.log('Start time changed:', e.target.value);
                    setNewActivity({ ...newActivity, startTime: e.target.value });
                  }}
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                  data-testid="create-activity-start-time"
                />
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">End Time</Label>
                <Input
                  type="time"
                  value={newActivity.endTime}
                  onChange={(e) => setNewActivity({ ...newActivity, endTime: e.target.value })}
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                  data-testid="create-activity-end-time"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#9CA3AF] text-xs">Description</Label>
              <Textarea
                value={newActivity.description}
                onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                placeholder="Activity details..."
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[80px] placeholder:text-[#6B7280]"
                data-testid="create-activity-description"
              />
            </div>

            {/* Reminder — how many hours before to notify */}
            <div>
              <Label className="text-[#9CA3AF] text-xs">Reminder</Label>
              <Select
                value={String(newActivity.remind_before_hours || 0)}
                onValueChange={(v) => setNewActivity({ ...newActivity, remind_before_hours: Number(v) })}
              >
                <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" data-testid="create-activity-remind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No reminder</SelectItem>
                  <SelectItem value="1">1 hour before</SelectItem>
                  <SelectItem value="3">3 hours before</SelectItem>
                  <SelectItem value="24">1 day before</SelectItem>
                  <SelectItem value="48">2 days before</SelectItem>
                  <SelectItem value="168">1 week before</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#6B7280] mt-1">
                Triggers in-app bell, iCal calendar alert (iPhone/Mac), and enables one-click Telegram push.
              </p>
            </div>
            
            {/* Validation feedback */}
            {(!newActivity.title || !newActivity.date || !newActivity.startTime) && (
              <p className="text-xs text-[#F59E0B]">* Please fill in all required fields</p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-[hsl(var(--border))] text-white">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateActivity}
              className="bg-gradient-to-r from-[#E63946] to-[#F97316] text-white disabled:opacity-50"
              disabled={!newActivity.title || !newActivity.date || !newActivity.startTime}
              data-testid="create-activity-submit"
            >
              Create Activity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Activity Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 size={20} className="text-[#3B82F6]" />
              Edit Activity
            </DialogTitle>
          </DialogHeader>
          
          {editingActivity && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#9CA3AF] text-xs">Activity Title *</Label>
                <Input
                  value={editingActivity.title}
                  onChange={(e) => setEditingActivity({ ...editingActivity, title: e.target.value })}
                  placeholder="e.g., Morning Live Trade Session"
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 placeholder:text-[#6B7280]"
                  data-testid="edit-activity-title"
                />
              </div>
              
              <div>
                <Label className="text-[#9CA3AF] text-xs">Activity Type *</Label>
                <Select value={editingActivity.type} onValueChange={(v) => setEditingActivity({ ...editingActivity, type: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" data-testid="edit-activity-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    {Object.entries(activityTypes).map(([key, config]) => (
                      <SelectItem key={key} value={key} className="text-white">
                        <div className="flex items-center gap-2">
                          {React.createElement(config.icon, { size: 14, style: { color: config.color } })}
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-[#9CA3AF] text-xs">Date *</Label>
                <Input
                  type="date"
                  value={editingActivity.date}
                  onChange={(e) => setEditingActivity({ ...editingActivity, date: e.target.value })}
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                  data-testid="edit-activity-date"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#9CA3AF] text-xs">Start Time *</Label>
                  <Input
                    type="time"
                    value={editingActivity.startTime}
                    onChange={(e) => setEditingActivity({ ...editingActivity, startTime: e.target.value })}
                    className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                    data-testid="edit-activity-start-time"
                  />
                </div>
                <div>
                  <Label className="text-[#9CA3AF] text-xs">End Time</Label>
                  <Input
                    type="time"
                    value={editingActivity.endTime}
                    onChange={(e) => setEditingActivity({ ...editingActivity, endTime: e.target.value })}
                    className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                    data-testid="edit-activity-end-time"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-[#9CA3AF] text-xs">Status</Label>
                <Select value={editingActivity.status} onValueChange={(v) => setEditingActivity({ ...editingActivity, status: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" data-testid="edit-activity-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key} className="text-white">
                        <div className="flex items-center gap-2">
                          {React.createElement(config.icon, { size: 14, style: { color: config.color } })}
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-[#9CA3AF] text-xs">Description</Label>
                <Textarea
                  value={editingActivity.description}
                  onChange={(e) => setEditingActivity({ ...editingActivity, description: e.target.value })}
                  placeholder="Activity details..."
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[80px] placeholder:text-[#6B7280]"
                  data-testid="edit-activity-description"
                />
              </div>

              {/* Post-event Notes — Outcome / Impact / Action */}
              <div className="pt-3 mt-2 border-t border-[hsl(var(--border))]">
                <div className="flex items-center gap-2 mb-3">
                  <StickyNote size={14} className="text-[#F59E0B]" />
                  <p className="text-xs font-semibold text-white">Post-event Notes</p>
                  <span className="text-[10px] text-[#6B7280]">Fill these in after activity ends</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-[#9CA3AF] text-[10px] uppercase tracking-wider flex items-center gap-1.5"><Check size={11} className="text-[#10B981]" /> Outcome</Label>
                    <Textarea
                      value={editingActivity.outcome || ''}
                      onChange={(e) => setEditingActivity({ ...editingActivity, outcome: e.target.value })}
                      placeholder="What happened? e.g. Win 50 pips XAUUSD, 200 viewers..."
                      className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[60px] placeholder:text-[#6B7280] text-xs"
                      data-testid="edit-activity-outcome"
                    />
                  </div>
                  <div>
                    <Label className="text-[#9CA3AF] text-[10px] uppercase tracking-wider flex items-center gap-1.5"><TrendingUp size={11} className="text-[#F59E0B]" /> Impact</Label>
                    <Textarea
                      value={editingActivity.impact || ''}
                      onChange={(e) => setEditingActivity({ ...editingActivity, impact: e.target.value })}
                      placeholder="Effect on subs / sales / engagement..."
                      className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[60px] placeholder:text-[#6B7280] text-xs"
                      data-testid="edit-activity-impact"
                    />
                  </div>
                  <div>
                    <Label className="text-[#9CA3AF] text-[10px] uppercase tracking-wider flex items-center gap-1.5"><Target size={11} className="text-[#3B82F6]" /> Action</Label>
                    <Textarea
                      value={editingActivity.action || ''}
                      onChange={(e) => setEditingActivity({ ...editingActivity, action: e.target.value })}
                      placeholder="Next steps / follow-up actions..."
                      className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[60px] placeholder:text-[#6B7280] text-xs"
                      data-testid="edit-activity-action"
                    />
                  </div>
                </div>
              </div>
              
              {/* Validation feedback */}
              {(!editingActivity.title || !editingActivity.date || !editingActivity.startTime) && (
                <p className="text-xs text-[#F59E0B]">* Please fill in all required fields</p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-[hsl(var(--border))] text-white">
              Cancel
            </Button>
            <Button 
              onClick={handleEditActivity}
              className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white disabled:opacity-50"
              disabled={!editingActivity?.title || !editingActivity?.date || !editingActivity?.startTime}
              data-testid="edit-activity-submit"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== ACTIVITY DETAILS / NOTES POPUP ========== */}
      <Dialog open={showDetailsModal} onOpenChange={(v) => { setShowDetailsModal(v); if (!v) setViewingActivity(null); }}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="activity-details-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye size={20} className="text-[#8B5CF6]" />
              Activity Details
            </DialogTitle>
          </DialogHeader>

          {viewingActivity && (() => {
            const tCfg = activityTypes[viewingActivity.type] || activityTypes.other;
            const sCfg = statusConfig[viewingActivity.status] || statusConfig.planned;
            const ActIcon = tCfg.icon;
            const StatIcon = sCfg.icon;
            return (
              <div className="space-y-4 py-2">
                {/* Header info */}
                <div className="flex items-start gap-3 p-3 rounded-xl border" style={{ backgroundColor: tCfg.bgColor, borderColor: `${tCfg.color}40` }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${tCfg.color}30` }}>
                    <ActIcon size={18} style={{ color: tCfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-white">{viewingActivity.title}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <Badge style={{ backgroundColor: `${tCfg.color}20`, color: tCfg.color, borderColor: `${tCfg.color}50` }} className="text-[10px]">{tCfg.label}</Badge>
                      <Badge style={{ backgroundColor: `${sCfg.color}20`, color: sCfg.color, borderColor: `${sCfg.color}50` }} className="text-[10px]"><StatIcon size={10} className="mr-1" />{sCfg.label}</Badge>
                      <span className="text-[11px] text-[#9CA3AF]">{new Date(viewingActivity.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span className="text-[11px] text-[#6B7280]">{viewingActivity.startTime} – {viewingActivity.endTime}</span>
                    </div>
                  </div>
                </div>

                {viewingActivity.description && (
                  <div className="p-3 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-bold">Description</p>
                    <p className="text-xs text-[#E0E0E5] whitespace-pre-wrap">{viewingActivity.description}</p>
                  </div>
                )}

                {/* Audit trail — who created / approved / completed */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {viewingActivity.created_by_name && (
                    <div className="p-2 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                      <p className="text-[9px] uppercase tracking-wider text-[#6B7280] font-bold mb-0.5">Created by</p>
                      <p className="text-[11px] text-white font-medium" data-testid="details-created-by">{viewingActivity.created_by_name}</p>
                    </div>
                  )}
                  {viewingActivity.approved_by_name && (
                    <div className="p-2 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                      <p className="text-[9px] uppercase tracking-wider text-[#6B7280] font-bold mb-0.5">{viewingActivity.approval_status === 'rejected' ? 'Rejected by' : 'Approved by'}</p>
                      <p className="text-[11px] text-white font-medium" data-testid="details-approved-by">{viewingActivity.approved_by_name}</p>
                    </div>
                  )}
                  {viewingActivity.completed_by_name && (
                    <div className="p-2 rounded-lg bg-[#10B981]/5 border border-[#10B981]/30">
                      <p className="text-[9px] uppercase tracking-wider text-[#10B981] font-bold mb-0.5">Completed by (scorecard credit)</p>
                      <p className="text-[11px] text-white font-medium" data-testid="details-completed-by">{viewingActivity.completed_by_name}</p>
                    </div>
                  )}
                </div>

                {/* Outcome */}
                <div className="p-3 rounded-lg bg-[#10B981]/5 border border-[#10B981]/30">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Check size={12} className="text-[#10B981]" />
                      <p className="text-[10px] uppercase tracking-wider text-[#10B981] font-bold">Outcome</p>
                    </div>
                  </div>
                  {viewingActivity.outcome ? (
                    <p className="text-xs text-[#E0E0E5] whitespace-pre-wrap" data-testid="details-outcome">{viewingActivity.outcome}</p>
                  ) : (
                    <p className="text-[11px] italic text-[#6B7280]">No outcome noted yet</p>
                  )}
                </div>

                {/* Impact */}
                <div className="p-3 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/30">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp size={12} className="text-[#F59E0B]" />
                      <p className="text-[10px] uppercase tracking-wider text-[#F59E0B] font-bold">Impact</p>
                    </div>
                  </div>
                  {viewingActivity.impact ? (
                    <p className="text-xs text-[#E0E0E5] whitespace-pre-wrap" data-testid="details-impact">{viewingActivity.impact}</p>
                  ) : (
                    <p className="text-[11px] italic text-[#6B7280]">No impact noted yet</p>
                  )}
                </div>

                {/* Action */}
                <div className="p-3 rounded-lg bg-[#3B82F6]/5 border border-[#3B82F6]/30">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Target size={12} className="text-[#3B82F6]" />
                      <p className="text-[10px] uppercase tracking-wider text-[#3B82F6] font-bold">Action</p>
                    </div>
                  </div>
                  {viewingActivity.action ? (
                    <p className="text-xs text-[#E0E0E5] whitespace-pre-wrap" data-testid="details-action">{viewingActivity.action}</p>
                  ) : (
                    <p className="text-[11px] italic text-[#6B7280]">No action noted yet</p>
                  )}
                </div>
              </div>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDetailsModal(false); setViewingActivity(null); }} className="border-[hsl(var(--border))] text-white">Close</Button>
            <Button
              onClick={() => { if (viewingActivity) { setShowDetailsModal(false); openEditModal(viewingActivity); } }}
              className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white"
              data-testid="details-edit-btn"
            >
              <Edit2 size={14} className="mr-1.5" />
              Edit Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Month Modal — bulk-duplicate every activity from current month to another month */}
      <Dialog open={showCopyMonthModal} onOpenChange={setShowCopyMonthModal}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Copy size={18} className="text-[#8B5CF6]" />
              Copy Month
            </DialogTitle>
            <div
              className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-md border bg-gradient-to-r ${channelTheme[channelType].accent} ${channelTheme[channelType].border}`}
            >
              {React.createElement(channelTheme[channelType].icon, { size: 14, style: { color: channelTheme[channelType].color } })}
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: channelTheme[channelType].color }}>
                {ib?.name || 'Channel'} • {channelTheme[channelType].label}
              </span>
              <span className="ml-auto text-[9px] text-[hsl(var(--text-muted))]">
                Tukar toggle di header kalau salah channel.
              </span>
            </div>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <Label className="text-xs text-[hsl(var(--text-muted))] mb-1.5 block uppercase tracking-wider">Source month (semua aktiviti dalam bulan ini akan di-copy)</Label>
              <div className="p-3 rounded-md bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                <p className="text-white text-sm font-semibold" data-testid="copy-month-source-label">
                  {currentDate.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })}
                </p>
                <p className="text-[10px] text-[hsl(var(--text-muted))] mt-0.5">
                  {(() => {
                    const srcY = currentDate.getFullYear();
                    const srcM = currentDate.getMonth();
                    const count = activities.filter(a => a?.channel === channelType && a.date && new Date(a.date).getFullYear() === srcY && new Date(a.date).getMonth() === srcM && (copyMonthIncludeCompleted || a.status === 'planned')).length;
                    return `${count} aktiviti dijumpai (mengikut filter di bawah)`;
                  })()}
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs text-[hsl(var(--text-muted))] mb-1.5 block uppercase tracking-wider">Target month</Label>
              <Input
                type="month"
                value={copyMonthTarget}
                onChange={(e) => setCopyMonthTarget(e.target.value)}
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]"
                data-testid="copy-month-target-input"
              />
              <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">
                Hari dalam bulan akan kekal sama (e.g. 15 Jun → 15 Jul). Hari yang tak wujud (e.g. 31 → Feb) akan di-skip.
              </p>
            </div>

            <label className="flex items-center gap-2 text-xs text-[hsl(var(--text-muted))] cursor-pointer">
              <input
                type="checkbox"
                checked={copyMonthIncludeCompleted}
                onChange={(e) => setCopyMonthIncludeCompleted(e.target.checked)}
                className="accent-[#8B5CF6]"
                data-testid="copy-month-include-completed"
              />
              Termasuk juga aktiviti yang sudah <span className="text-emerald-400">Done</span> / <span className="text-red-400">Missed</span> (default: planned sahaja)
            </label>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCopyMonthModal(false)} className="border-[hsl(var(--border))]">
              Cancel
            </Button>
            <Button
              onClick={runCopyMonth}
              className="bg-gradient-to-r from-[#8B5CF6] to-[#A855F7] hover:from-[#8B5CF6]/90 text-white"
              data-testid="copy-month-submit"
              disabled={!copyMonthTarget}
            >
              <Copy size={14} className="mr-1.5" />
              Copy ke {copyMonthTarget ? new Date(`${copyMonthTarget}-01T00:00:00`).toLocaleDateString('en-MY', { month: 'short', year: 'numeric' }) : '...'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Report Modal — chart-driven overview of the current month */}
      <ActivitySummaryReportModal
        open={showSummaryReport}
        onOpenChange={setShowSummaryReport}
        activities={activities}
        channelType={channelType}
        currentDate={currentDate}
        ib={ib}
      />
    </>
  );
}
